import { userAttendee } from '@common/features/User/models/attendee'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FreeBusyIndicator } from './FreeBusyIndicator'
import {
  ExtendedAutocompleteRenderInputParams,
  PeopleSearch
} from './PeopleSearch'
import { dedupeByEmail } from './usePeopleSearchState'
import {
  DirectorySurface,
  resolveCorporateDirectorySelections
} from './directory'
import { User } from './types'
import { FreeBusyStatus, useAttendeesFreeBusy } from './useFreeBusy'

const attendeeToUser = (a: userAttendee, openpaasId = ''): User => ({
  email: a.cal_address,
  displayName: a.cn ?? '',
  avatarUrl: '',
  openpaasId
})

const hasCalendar = (u: User): boolean =>
  u.objectType === 'user' && !!u.openpaasId

function useAttendeeSearchLogic({
  attendees,
  eventUid,
  start,
  end,
  timezone
}: {
  attendees: userAttendee[]
  eventUid?: string | null
  start?: string
  end?: string
  timezone?: string
}): {
  selectedUsers: User[]
  initialEmails: Set<string>
  statusMap: Record<string, FreeBusyStatus>
  setUserIdMap: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setAddedUsers: React.Dispatch<React.SetStateAction<User[]>>
} {
  const [userIdMap, setUserIdMap] = useState<Record<string, string>>({})
  const [addedUsers, setAddedUsers] = useState<User[]>([])
  const initialEmailsRef = useRef<Set<string> | null>(null)
  const [initialEmailsSet, setInitialEmailsSet] = useState<Set<string>>(
    new Set()
  )

  const prevEventUidRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const updateInitialEmailsSet = (): void => {
      // Reset when switching events
      if (prevEventUidRef.current !== eventUid) {
        initialEmailsRef.current = null
        prevEventUidRef.current = eventUid
      }

      const shouldInitializeInitialEmails =
        initialEmailsRef.current === null && !!eventUid && attendees.length > 0

      if (shouldInitializeInitialEmails) {
        initialEmailsRef.current = new Set(attendees.map(a => a.cal_address))
        setInitialEmailsSet(initialEmailsRef.current)
      }
    }
    updateInitialEmailsSet()
  }, [eventUid, attendees])

  const initialEmails = useMemo(
    () => (eventUid ? initialEmailsSet : new Set<string>()),
    [eventUid, initialEmailsSet]
  )

  const selectedUsers = useMemo(
    () => [
      ...addedUsers,
      ...attendees
        .map(a => attendeeToUser(a, userIdMap[a.cal_address]))
        .filter(a => !addedUsers.find(u => a.email === u.email))
    ],
    [addedUsers, attendees, userIdMap]
  )

  const categorization = useMemo(() => {
    const toAttendee = (u: User): { email: string; userId: string | null } => ({
      email: u.email,
      userId: u.openpaasId || userIdMap[u.email] || null
    })

    const existing = selectedUsers
      .filter(u => initialEmails.has(u.email))
      .map(toAttendee)

    const nextNew = selectedUsers
      .filter(u => !initialEmails.has(u.email) && hasCalendar(u))
      .map(toAttendee)

    const contacts = Object.fromEntries(
      selectedUsers
        .filter(u => !initialEmails.has(u.email) && !hasCalendar(u))
        .map(u => [u.email, 'contact' as const])
    )

    return { existing, nextNew, contacts }
  }, [selectedUsers, initialEmails, userIdMap])

  const freeBusyMap = useAttendeesFreeBusy({
    existingAttendees: categorization.existing,
    newAttendees: categorization.nextNew,
    start: start ?? '',
    end: end ?? '',
    timezone: timezone ?? '',
    eventUid,
    enabled: !!(start && end && selectedUsers.length > 0)
  })

  return {
    selectedUsers,
    initialEmails,
    statusMap: { ...freeBusyMap, ...categorization.contacts },
    setUserIdMap,
    setAddedUsers
  }
}

export const AttendeeSearch: React.FC<{
  attendees: userAttendee[]
  setAttendees: (attendees: userAttendee[]) => void
  disabled?: boolean
  inputSlot?: (params: ExtendedAutocompleteRenderInputParams) => React.ReactNode
  placeholder?: string
  start?: string
  end?: string
  timezone?: string
  eventUid?: string | null
  directorySurface?: DirectorySurface
}> = ({
  attendees,
  setAttendees,
  disabled,
  inputSlot,
  placeholder,
  start,
  end,
  timezone,
  eventUid,
  directorySurface = 'calendar'
}) => {
  const {
    selectedUsers,
    initialEmails,
    statusMap,
    setUserIdMap,
    setAddedUsers
  } = useAttendeeSearchLogic({ attendees, eventUid, start, end, timezone })

  const [directoryError, setDirectoryError] = useState<string | null>(null)

  const handleOnChange = async (
    _event: React.SyntheticEvent,
    value: User[]
  ): Promise<void> => {
    const groups = value.filter(
      user => user.kind === 'group' || user.objectType === 'group'
    )
    let nextUsers = value.filter(
      user => user.kind !== 'group' && user.objectType !== 'group'
    )
    if (groups.length && directorySurface) {
      try {
        const expanded = await resolveCorporateDirectorySelections(
          groups
            .map(group => ({
              kind: 'group' as const,
              id: group.directoryId || ''
            }))
            .filter(selection => selection.id),
          directorySurface
        )
        nextUsers = dedupeByEmail([...nextUsers, ...expanded])
        setDirectoryError(null)
      } catch {
        setDirectoryError(
          'Не удалось раскрыть выбранную группу. Попробуйте еще раз.'
        )
        return
      }
    } else {
      setDirectoryError(null)
    }
    setUserIdMap(prev => {
      const next = { ...prev }
      nextUsers.forEach(u => {
        if (u.openpaasId && u.email) next[u.email] = u.openpaasId
      })
      return next
    })
    setAddedUsers(nextUsers.filter(u => !initialEmails.has(u.email)))
    setAttendees(
      nextUsers.map(
        u => new userAttendee({ cal_address: u.email, cn: u.displayName })
      )
    )
  }

  return (
    <PeopleSearch
      selectedUsers={selectedUsers}
      showCurrentUser
      objectTypes={['user']}
      disabled={disabled}
      directorySurface={directorySurface}
      directoryOpenOnFocus={Boolean(directorySurface)}
      directoryError={directoryError}
      inputSlot={inputSlot}
      placeholder={placeholder}
      getChipIcon={
        start && end
          ? (user): JSX.Element => (
              <FreeBusyIndicator status={statusMap[user.email] ?? 'unknown'} />
            )
          : undefined
      }
      onChange={(event, value): void => {
        void handleOnChange(event, value)
      }}
      freeSolo
      enableEmailAutocompleteAndCommit
    />
  )
}

export default AttendeeSearch
